import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@azim-crm/icons";
import styles from "./EditableName.module.css";
/**
 * Nome editável no lugar, do Azim CRM.
 * Entra em edição por clique ou teclado (Enter/Espaço no gatilho nativo),
 * salva com Enter, cancela com Esc devolvendo o foco ao gatilho.
 * Um erro do chamador nunca apaga o texto digitado.
 */
export function EditableName({ value, onSave, label, disabled = false, muted = false, maxLength, error }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const [saving, setSaving] = useState(false);
    const triggerRef = useRef(null);
    const inputRef = useRef(null);
    // O botão-gatilho só existe no DOM depois que `editing` volta a `false` e o
    // React re-renderiza — focar sincronamente dentro de cancel()/commit() ainda
    // pegaria o elemento antigo (ou nenhum). O efeito roda após o commit do DOM.
    const shouldRefocusTrigger = useRef(false);
    useEffect(() => {
        if (!editing)
            setDraft(value);
    }, [value, editing]);
    useEffect(() => {
        if (!editing && shouldRefocusTrigger.current) {
            shouldRefocusTrigger.current = false;
            triggerRef.current?.focus();
        }
    }, [editing]);
    // Um erro informado pelo chamador força a edição a permanecer aberta, mesmo
    // que o componente já tivesse decidido fechar — o texto digitado nunca some.
    useEffect(() => {
        if (error)
            setEditing(true);
    }, [error]);
    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);
    const startEditing = () => {
        if (disabled)
            return;
        setDraft(value);
        setEditing(true);
    };
    const cancel = () => {
        setDraft(value);
        shouldRefocusTrigger.current = true;
        setEditing(false);
    };
    const commit = async () => {
        const next = draft.trim();
        setSaving(true);
        try {
            await onSave(next);
            shouldRefocusTrigger.current = true;
            setEditing(false);
        }
        catch {
            // A falha é comunicada pelo chamador via prop `error`; o rascunho
            // permanece intacto e a edição continua aberta.
        }
        finally {
            setSaving(false);
        }
    };
    const handleInputChange = (e) => {
        setDraft(e.target.value);
    };
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            void commit();
        }
        else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
        }
    };
    if (editing) {
        return (_jsxs("div", { className: styles.editing, children: [_jsx("input", { ref: inputRef, type: "text", className: [styles.input, error ? styles.inputError : ""].filter(Boolean).join(" "), value: draft, maxLength: maxLength, disabled: saving, "aria-label": label, "aria-invalid": Boolean(error), onChange: handleInputChange, onKeyDown: handleKeyDown }), _jsx("button", { type: "button", className: styles.iconButton, onClick: () => void commit(), disabled: saving, "aria-label": `Salvar ${label}`, children: _jsx(Icon, { name: "check", size: 15 }) }), _jsx("button", { type: "button", className: styles.iconButton, onClick: cancel, disabled: saving, "aria-label": `Cancelar edição de ${label}`, children: _jsx(Icon, { name: "x", size: 15 }) }), error && (_jsx("span", { role: "alert", className: styles.error, children: error }))] }));
    }
    return (_jsxs("div", { className: styles.display, children: [_jsx("span", { className: [styles.value, muted ? styles.muted : ""].filter(Boolean).join(" "), children: value }), _jsx("button", { ref: triggerRef, type: "button", className: styles.trigger, onClick: startEditing, disabled: disabled, "aria-label": `Editar ${label}`, children: _jsx(Icon, { name: "edit", size: 14 }) })] }));
}
//# sourceMappingURL=EditableName.js.map